/* src/contexts/GameContext.js */
import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useRef,
    useCallback,
    useMemo
} from 'react';
import { useProfile } from './ProfileContext';
import { useSettings } from './SettingsContext';
import audioManager from '../utils/audioManager';
import { createGameActions } from './game/gameActions';
import { createGameSetupActions } from './game/gameSetup';
import { createEndGameAction } from './game/gameEnd';
import { createChallengeActions } from './game/gameChallenge';
import { createGameGetters } from './game/gameGetters';
import { createLogReplayEvent } from './game/replayUtils';
import { useLoading } from './LoadingContext';

export const GameContext = createContext(null);

export const useGame = () => {
    const context = useContext(GameContext);
    if (!context) { throw new Error('useGame must be used within a GameProvider'); }
    return context;
};

// Helper function for NEW weighted random ISI variability
function getWeightedRandomVariability(maxOffset) {
    // If maxOffset is 0 (e.g., for difficulties below N.60),
    // no variability should be added.
    if (maxOffset <= 0) {
        return 0;
    }

    const probRoll = Math.random(); // Random number between 0.0 (inclusive) and 1.0 (exclusive)

    // Define probabilities for each bucket
    const probZeroAdded = 0.55;       // 55% chance of 0ms added variability
    const probMaxRangeTarget = 0.10;  // Target 10% chance for the "max variability" bucket
    // The probMidRange is implicitly 1.0 - probZeroAdded - probMaxRangeTarget = 0.35

    let selectedRandomMs;

    // Bucket 1: Zero Added Variability (55% chance)
    if (probRoll < probZeroAdded) { // True for probRoll in [0, 0.55)
        selectedRandomMs = 0;
    }
    // Bucket 2: Maximum Added Variability Range (next 10% chance)
    // True for probRoll in [0.55, 0.65)
    else if (probRoll < probZeroAdded + probMaxRangeTarget) { 
        // Value from the upper 25% of maxOffset
        const upperRangeStart = maxOffset * 0.75;
        // Ensure the range has some width, especially if maxOffset is small
        // If maxOffset is 1, 2, or 3, then 0.75*maxOffset will be < maxOffset, so the range (maxOffset - upperRangeStart) will be > 0
        // If maxOffset is 0, we already returned.
        if (upperRangeStart >= maxOffset) { 
             // This edge case occurs if maxOffset is extremely small (e.g., < 4ms such that 0.75*maxOffset rounds up to maxOffset)
             // or if maxOffset itself is 0 (though handled at the start).
             // To ensure some variability if maxOffset > 0, just return maxOffset.
            selectedRandomMs = maxOffset;
        } else {
            selectedRandomMs = upperRangeStart + (Math.random() * (maxOffset - upperRangeStart));
        }
    }
    // Bucket 3: Medium Added Variability (remaining 35% chance)
    // True for probRoll in [0.65, 1.0)
    else {
        // Value from 1ms up to (but not including) the start of the "Maximum Added" range (0.75 * maxOffset)
        const midRangeExclusiveEnd = maxOffset * 0.75;

        if (midRangeExclusiveEnd <= 1) { 
            // If 75% of maxOffset is 1ms or less, it's hard to get a varied random number
            // that isn't 0 (already handled) or exactly 1.
            // If maxOffset itself is > 0 (guaranteed by the first check in this function), let's just return 1ms.
            selectedRandomMs = 1;
        } else {
            // We want a random number in [1, midRangeExclusiveEnd).
            // Math.random() * (range_size) gives [0, range_size)
            // range_size = midRangeExclusiveEnd - 1
            // So, 1 + (Math.random() * (midRangeExclusiveEnd - 1)) gives [1, midRangeExclusiveEnd)
            selectedRandomMs = 1 + (Math.random() * (midRangeExclusiveEnd - 1));
        }
    }

    return Math.floor(selectedRandomMs);
}


export const GameProvider = ({ children }) => {
    // --- State Definitions ---
    const [gameState, setGameState] = useState('idle');
    const [pauseReason, setPauseReason] = useState(null);
    const [nBack, setNBack] = useState(2);
    const [difficulty, setDifficulty] = useState(0.0);
    const [sequence, setSequence] = useState([]);
    const [currentTrial, setCurrentTrial] = useState(-1);
    const [totalTrials, setTotalTrials] = useState(0);
    const [gameResults, setGameResults] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [consecutiveSuccesses, setConsecutiveSuccesses] = useState(0);
    const [showCountdown, setShowCountdown] = useState(false);
    const [saveToProfile, setSaveToProfile] = useState(true);
    const [isPlaygroundSession, setIsPlaygroundSession] = useState(false);
    const [playgroundSettings, setPlaygroundSettings] = useState(null);
    const [challengeState, setChallengeState] = useState({ isActive: false, targetNBack: null, wins: 0 });
    const [isCountdownVisuallyComplete, setIsCountdownVisuallyComplete] = useState(false);

    // --- Refs ---
    const visualMatchesRef = useRef([]);
    const audioMatchesRef = useRef([]);
    const visualResponsesRef = useRef([]);
    const audioResponsesRef = useRef([]);
    const timerRef = useRef(null);
    const isEndingRef = useRef(false);
    const sessionSettingsRef = useRef(null);
    const replayEventsRef = useRef([]);
    const sessionStartTimeRef = useRef(null);
    const onNavigateRef = useRef(null);
    const latestGameStateRef = useRef(gameState);
    const currentTrialRef = useRef(currentTrial);
    const totalTrialsRef = useRef(totalTrials);
    const nBackRef = useRef(nBack);
    const difficultyRef = useRef(difficulty);
    const consecutiveSuccessesRef = useRef(consecutiveSuccesses);
    const audioTriggeredForTrial = useRef(-1);

    // --- External Contexts ---
    const { activeProfile, updateProfileProgress } = useProfile();
    const { settings } = useSettings(); // settings is available here
    const { showLoading, hideLoading } = useLoading();

    // --- Initialization Effects ---
    useEffect(() => { audioManager.init(); }, []);

    // --- Ref Sync Effects ---
    useEffect(() => { latestGameStateRef.current = gameState; }, [gameState]);
    useEffect(() => { currentTrialRef.current = currentTrial; }, [currentTrial]);
    useEffect(() => { totalTrialsRef.current = totalTrials; }, [totalTrials]);
    useEffect(() => { nBackRef.current = nBack; }, [nBack]);
    useEffect(() => { difficultyRef.current = difficulty; }, [difficulty]);
    useEffect(() => { consecutiveSuccessesRef.current = consecutiveSuccesses; }, [consecutiveSuccesses]);

    // --- Replay Logging Action ---
    const logReplayEvent = useCallback(createLogReplayEvent(replayEventsRef), [replayEventsRef]);

    // --- Memoized Action Logics (Created via Factory Functions) ---
    const {
        pauseGameLogic, resumeGameLogic, clearPauseReasonLogic,
        playAfterCountdownLogic, registerResponseLogic,
        setManualSettingsLogic, resetContextResponseArraysLogic
    } = useMemo(() => createGameActions({
        setGameState, setNBack, setDifficulty, setSaveToProfile, setCurrentTrial,
        setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason,
        latestGameStateRef, visualResponsesRef, audioResponsesRef, logReplayEvent,
        timerRef, sessionStartTimeRef, isEndingRef, audioManager
    }), [
        logReplayEvent, setGameState, setNBack, setDifficulty, setSaveToProfile,
        setCurrentTrial, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason
    ]);

    const startCountdown = useCallback(() => {
         audioManager.resume().then(resumed => {
            if (resumed) {
                setGameState('preparing'); setPauseReason(null); setFeedbackMessage('');
                setShowCountdown(true); setIsCountdownVisuallyComplete(false);
                audioTriggeredForTrial.current = -1;
                const isPlayground = isPlaygroundSession;
                const n = isPlayground ? playgroundSettings?.nBack : activeProfile?.currentNBack;
                const d = isPlayground ? playgroundSettings?.difficulty : activeProfile?.currentDifficulty;
                const voice = settings?.selectedVoiceId;
                const actualN = n ?? 2; const actualD = d ?? 0.0;
                const difficultySeedForPreload = Math.max(0, Math.min(1.0, actualD));
                if (voice) { audioManager.preload({ difficulties: [difficultySeedForPreload], voiceId: voice }); }
                else { console.warn("[GameContext->startCountdown] Cannot preload audio: No voice selected."); }
            } else {
                 console.error("[GameContext->startCountdown] Failed to resume audio context.");
                 setFeedbackMessage("Audio could not be started. Please click anywhere on the page or try again.");
            }
        });
    }, [
        setGameState, setPauseReason, setFeedbackMessage, setShowCountdown, setIsCountdownVisuallyComplete,
        isPlaygroundSession, playgroundSettings, activeProfile, settings
    ]);

    const { startGameLogic, startPlaygroundGameLogic, cancelPreparationLogic } = useMemo(() => createGameSetupActions({
        activeProfile,
        settings,
        saveToProfileState: saveToProfile,
        challengeState,
        isPlaygroundSessionState: isPlaygroundSession,
        setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
        setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
        setSaveToProfile, setPlaygroundSettings, startCountdown, setChallengeState,
        setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason,
        timerRef, isEndingRef, visualResponsesRef, audioResponsesRef, visualMatchesRef,
        audioMatchesRef, sessionSettingsRef, replayEventsRef, sessionStartTimeRef,
        onNavigateRef, latestGameStateRef, audioManager
    }), [
        activeProfile,
        settings,
        saveToProfile,
        challengeState,
        isPlaygroundSession,
        startCountdown,
        setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
        setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
        setSaveToProfile, setPlaygroundSettings, setChallengeState,
        setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason
    ]);

    const { enterChallengeLogic, exitChallengeLogic, completeChallengeLogic } = useMemo(() => createChallengeActions({
        activeProfile, updateProfileProgress, challengeState, setChallengeState, setGameResults, setGameState,
        setNBack, setDifficulty, setConsecutiveSuccesses, setFeedbackMessage, onNavigateRef, setPauseReason
    }), [
        activeProfile, updateProfileProgress, challengeState,
        setChallengeState, setGameResults, setGameState, setNBack, setDifficulty,
        setConsecutiveSuccesses, setFeedbackMessage, setPauseReason
    ]);

    const endGameLogic = useMemo(() => createEndGameAction({
        activeProfile, isPlaygroundSession, saveToProfile, challengeState, setChallengeState, logReplayEvent,
        setGameState, setGameResults, setFeedbackMessage, setIsPlaygroundSession, setPlaygroundSettings,
        setCurrentTrial, setSequence, setTotalTrials, setNBack, setDifficulty, setConsecutiveSuccesses,
        setSaveToProfile, updateProfileProgress, completeChallengeLogic, setPauseReason,
        latestGameStateRef, nBackRef, difficultyRef, consecutiveSuccessesRef, currentTrialRef, totalTrialsRef,
        onNavigateRef, isEndingRef, visualResponsesRef, audioResponsesRef,
        sessionSettingsRef, sessionStartTimeRef, replayEventsRef,
        showLoading, // Pass loading functions
        hideLoading
    }), [
        activeProfile, isPlaygroundSession, saveToProfile, challengeState, logReplayEvent,
        updateProfileProgress, completeChallengeLogic,
        setChallengeState, setGameState, setGameResults, setFeedbackMessage,
        setIsPlaygroundSession, setPlaygroundSettings, setCurrentTrial, setSequence,
        setTotalTrials, setNBack, setDifficulty, setConsecutiveSuccesses, setSaveToProfile,
        setPauseReason,
        showLoading, // Add to dependency array
        hideLoading
    ]);

    const {
        isMatchLogic, getStimulusDurationLogic, getInstantFeedbackEnabledLogic, getCoachingMessageLogic,
        getFeatureIntroMessageLogic, getVisualMatchesLogic, getAudioMatchesLogic, getUserVisualResponsesLogic,
        getUserAudioResponsesLogic, getPlaygroundSettingLogic
    } = useMemo(() => createGameGetters({
        sequence, nBackRef, difficultyRef, isPlaygroundSession, playgroundSettings, sessionSettingsRef,
        visualMatchesRef, audioMatchesRef, visualResponsesRef, audioResponsesRef, settings
    }), [
        sequence, isPlaygroundSession, playgroundSettings, settings
    ]);


    // --- Stable Action Callbacks (Exported via Context Value) ---
    const pauseGame = useCallback((reason = 'manual') => { pauseGameLogic(reason); }, [pauseGameLogic]);
    const resumeGame = useCallback(() => {
        audioManager.resume().then(resumed => {
            if(resumed) { resumeGameLogic(); }
            else {
                console.error("[GameContext->resumeGame] Failed to resume audio.");
                setFeedbackMessage("Could not resume audio. Please interact with the page and try again.");
                setGameState('paused');
            }
        });
    }, [resumeGameLogic, setFeedbackMessage, setGameState]);

    const clearPauseReason = useCallback(() => { clearPauseReasonLogic(); }, [clearPauseReasonLogic]);
    const playAfterCountdown = useCallback(playAfterCountdownLogic, [playAfterCountdownLogic]);
    const registerResponse = useCallback(registerResponseLogic, [registerResponseLogic]);
    const setManualSettings = useCallback(setManualSettingsLogic, [setManualSettingsLogic]);
    const resetContextResponseArrays = useCallback(resetContextResponseArraysLogic, [resetContextResponseArraysLogic]);
    const setNavigationHandler = useCallback((handler) => { onNavigateRef.current = handler; }, []);
    const startGame = useCallback(startGameLogic, [startGameLogic]);
    const startPlaygroundGame = useCallback(startPlaygroundGameLogic, [startPlaygroundGameLogic]);
    const cancelPreparation = useCallback(cancelPreparationLogic, [cancelPreparationLogic]);
    const enterChallenge = useCallback(enterChallengeLogic, [enterChallengeLogic]);
    const exitChallenge = useCallback(exitChallengeLogic, [exitChallengeLogic]);
    const completeChallenge = useCallback(completeChallengeLogic, [completeChallengeLogic]);

    const endGame = useCallback(async (options = { skipResultsProcessing: false }) => {
        await endGameLogic(options);
    }, [endGameLogic]);

    // --- NEW GAME ACTIONS for N-Level Navigation ---
    const goToPreviousNBackLevel = useCallback(async () => {
        if (!activeProfile || nBackRef.current <= 1) { // Technically should be nBackRef.current <= 2 if 1-back isn't offered
            console.warn("Cannot go to previous level: No profile or already at lowest playable level.");
            setFeedbackMessage("Already at the lowest N-Back level or no profile loaded.");
            return;
        }
        const targetNBack = nBackRef.current - 1;
        const previousDifficulties = activeProfile.previousNBackLevelDifficulties || {};
        const targetDifficulty = previousDifficulties[targetNBack] !== undefined ? previousDifficulties[targetNBack] : 0.0;

        console.log(`[GameContext] goToPreviousNBackLevel: Target N${targetNBack}.${Math.round(targetDifficulty*100).toString().padStart(2,'0')}`);
        if (latestGameStateRef.current !== 'idle' && latestGameStateRef.current !== 'results') {
             await endGameLogic({ skipResultsProcessing: true });
        }
        // Update profile: Set currentNBack and currentDifficulty to where we are going
        const profileUpdatePayload = {
            currentNBack: targetNBack,
            currentDifficulty: targetDifficulty,
            consecutiveSuccesses: 0, // Reset streak when manually changing levels
        };
        const updateSuccess = await updateProfileProgress(activeProfile.id, profileUpdatePayload);
        if (updateSuccess) {
            startGameLogic({ overrideNBack: targetNBack, overrideDifficulty: targetDifficulty });
        } else {
            console.error("[GameContext] Failed to update profile for goToPreviousNBackLevel.");
            setFeedbackMessage("Error saving progress. Could not go to previous level.");
        }
    }, [activeProfile, startGameLogic, endGameLogic, updateProfileProgress, setFeedbackMessage]);

    // **** MODIFIED: goToNextNBackLevelZero now accepts nBackJustPlayed and difficultyJustPlayed ****
    const goToNextNBackLevelZero = useCallback(async (nBackJustPlayed, difficultyJustPlayed) => {
        if (!activeProfile) {
            console.warn("Cannot go to next level: No profile.");
            setFeedbackMessage("No profile loaded.");
            return;
        }
        // nBackJustPlayed and difficultyJustPlayed are from the session that just finished.
        // These are passed from GameResults when user clicks "Next Level: (N+1).00"
        
        const targetNBack = nBackJustPlayed + 1;
        const targetDifficulty = 0.0; // Go to N.00 of the next level

        console.log(`[GameContext] goToNextNBackLevelZero: From N${nBackJustPlayed}.${Math.round(difficultyJustPlayed*100).toString().padStart(2,'0')} to N${targetNBack}.00`);

        // Store where the user left off the nBackJustPlayed level
        const newPreviousDifficulties = {
            ...(activeProfile.previousNBackLevelDifficulties || {}),
            [nBackJustPlayed]: difficultyJustPlayed // Store the difficulty of the level they are leaving
        };

        // Update profile: Set currentNBack and currentDifficulty to the new target N.00
        // Also update highestNBack if this new N-level is higher.
        const profileUpdatePayload = {
            currentNBack: targetNBack,
            currentDifficulty: targetDifficulty,
            consecutiveSuccesses: 0, // Reset streak
            highestNBack: Math.max(activeProfile.highestNBack || 0, targetNBack),
            previousNBackLevelDifficulties: newPreviousDifficulties,
        };

        const updateSuccess = await updateProfileProgress(activeProfile.id, profileUpdatePayload);

        if (updateSuccess) {
            if (latestGameStateRef.current !== 'idle' && latestGameStateRef.current !== 'results') {
                await endGameLogic({ skipResultsProcessing: true });
            }
            startGameLogic({ overrideNBack: targetNBack, overrideDifficulty: targetDifficulty });
        } else {
            console.error("[GameContext] Failed to update profile for goToNextNBackLevelZero.");
            setFeedbackMessage("Error saving progress. Could not advance level.");
        }
    }, [activeProfile, updateProfileProgress, startGameLogic, endGameLogic, setFeedbackMessage]);


    // --- Stable Getter Callbacks (Exported via Context Value) ---
    const isMatch = useCallback(isMatchLogic, [isMatchLogic]);
    const getStimulusDuration = useCallback(getStimulusDurationLogic, [getStimulusDurationLogic]);
    const getInstantFeedbackEnabled = useCallback(getInstantFeedbackEnabledLogic, [getInstantFeedbackEnabledLogic]);
    const getCoachingMessage = useCallback(getCoachingMessageLogic, [getCoachingMessageLogic]);
    const getFeatureIntroMessage = useCallback(getFeatureIntroMessageLogic, [getFeatureIntroMessageLogic]);
    const getVisualMatches = useCallback(getVisualMatchesLogic, [getVisualMatchesLogic]);
    const getAudioMatches = useCallback(getAudioMatchesLogic, [getAudioMatchesLogic]);
    const getUserVisualResponses = useCallback(getUserVisualResponsesLogic, [getUserVisualResponsesLogic]);
    const getUserAudioResponses = useCallback(getUserAudioResponsesLogic, [getUserAudioResponsesLogic]);
    const getPlaygroundSetting = useCallback(getPlaygroundSettingLogic, [getPlaygroundSettingLogic]);
    const playFeedbackSound = useCallback((type) => audioManager.playFeedbackSound(type), []);

    // --- Core Game Logic Effects ---
    useEffect(() => {
        if (isCountdownVisuallyComplete && gameState === 'preparing') {
            console.log("[GameContext Effect] Countdown visually complete, transitioning to 'playing'.");
            sessionStartTimeRef.current = Date.now(); // Set start time
            logReplayEvent({ type: 'gameStart', timestamp: sessionStartTimeRef.current });

            // Reset trial to -1 before setting to 'playing'.
            // The 'playing' state effect will handle advancing to trial 0.
            setCurrentTrial(-1);

            setGameState('playing'); // This will trigger the timer useEffect
            setPauseReason(null); // Clear any pause reason
            setIsCountdownVisuallyComplete(false); // Reset the flag
        }
    }, [isCountdownVisuallyComplete, gameState, logReplayEvent, setCurrentTrial, setGameState, setPauseReason, setIsCountdownVisuallyComplete]);

    useEffect(() => {
        if (gameState === 'playing' && currentTrial >= 0 && currentTrial < sequence.length && currentTrial !== audioTriggeredForTrial.current) {
            // console.log(`[GameContext Audio Effect] Conditions met for trial ${currentTrial}. audioTriggeredForTrial.current = ${audioTriggeredForTrial.current}`);
            const currentStimulus = sequence[currentTrial];
            if (currentStimulus?.letter) {
                const trialToPlay = currentTrial;
                audioTriggeredForTrial.current = trialToPlay;
                // console.log(`[GameContext Audio Effect] Playing letter '${currentStimulus.letter}' for trial ${trialToPlay}.`);
                queueMicrotask(() => {
                    if (latestGameStateRef.current === 'playing' && currentTrialRef.current === trialToPlay) {
                        // console.log(`[GameContext Audio Effect] Double check passed for playing letter '${currentStimulus.letter}'`);
                         const selectedVoiceId = settings?.selectedVoiceId;
                         if (!selectedVoiceId) { console.error(`[GameContext Audio Effect] No voice selected. Cannot play audio.`); setFeedbackMessage("Warning: No audio voice selected in settings."); return; }
                         audioManager.playItem(currentStimulus.letter, difficultyRef.current, { voiceId: selectedVoiceId });
                         const playedTimestamp = Date.now();
                         logReplayEvent({
                             type: 'audioStimulusStart',
                             trialIndex: trialToPlay,
                             timestamp: playedTimestamp,
                             letter: currentStimulus.letter,
                             voice: selectedVoiceId,
                             difficulty: difficultyRef.current
                         });
                    }
                    // else { console.log(`[GameContext Audio Effect] Double check FAILED for playing letter. State: ${latestGameStateRef.current}, Trial: ${currentTrialRef.current} vs ${trialToPlay}`); }
                });
            }
            // else { console.log(`[GameContext Audio Effect] No letter for trial ${currentTrial} or already triggered.`); }
        } else if (gameState !== 'playing' && gameState !== 'paused') { audioManager.stopCurrentSound(); }
    }, [gameState, currentTrial, sequence, settings, logReplayEvent, setFeedbackMessage]);

    useEffect(() => {
        // timerRef.current will now hold the setTimeout ID
        if (timerRef.current) { 
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (gameState === 'playing') {
            const isiMs = (sessionSettingsRef.current?.calculatedISI ?? 2.5) * 1000; // Base ISI in ms
            const baseVariabilityOffsetMs = sessionSettingsRef.current?.baseVariabilityOffsetMs ?? 0; // Should be 0 with current backend logic
            const maxRandomVariabilityOffsetMs = sessionSettingsRef.current?.maxRandomVariabilityOffsetMs ?? 0;

            if (isNaN(isiMs) || isiMs <= 0) { 
                console.error(`Invalid base ISI (${isiMs}ms). Game cannot proceed.`);
                setGameState('idle'); setFeedbackMessage("Error: Invalid game speed configuration."); return; 
            }
            
            // console.log(`[GameContext Timer Effect] Initializing for 'playing' state. BaseISI: ${isiMs}ms, BaseVar: ${baseVariabilityOffsetMs}ms, MaxRandVar: ${maxRandomVariabilityOffsetMs}ms. CurrentTrial: ${currentTrialRef.current}`);

            const advanceAndScheduleNext = () => {
                if (isEndingRef.current || latestGameStateRef.current !== 'playing') {
                    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                    return;
                }

                setCurrentTrial(prevTrial => {
                    const nextTrial = prevTrial + 1;
                    const totalTrialsFromRef = totalTrialsRef.current;

                    if (nextTrial >= totalTrialsFromRef) {
                        // console.log(`Trial ${nextTrial}/${totalTrialsFromRef}. Reached end of sequence. Ending game.`);
                        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                        queueMicrotask(() => endGame()); 
                        return prevTrial; 
                    } else {
                        logReplayEvent({ type: 'trialStart', trialIndex: nextTrial, timestamp: Date.now() });
                        
                        const randomVariabilityMs = getWeightedRandomVariability(maxRandomVariabilityOffsetMs);

                        const currentTotalIsiMs = isiMs + baseVariabilityOffsetMs + randomVariabilityMs; // baseVariabilityOffsetMs is likely 0
                        const nextTimeoutDurationMs = Math.max(100, currentTotalIsiMs); 

                        // console.log(`[GameContext Timer] Trial ${nextTrial} displayed. Next trial in ${nextTimeoutDurationMs.toFixed(0)}ms (Base: ${isiMs.toFixed(0)}, BaseVarFixed: ${baseVariabilityOffsetMs}, RandVarWeighted: ${randomVariabilityMs.toFixed(0)})`);
                        timerRef.current = setTimeout(advanceAndScheduleNext, nextTimeoutDurationMs);
                        return nextTrial;
                    }
                });
            };

            // Determine the delay for the *first* advancement (from currentTrial -1 to 0)
            let initialDelayMs;
            if (currentTrialRef.current === -1) { // This is the very start of the game, after countdown
                initialDelayMs = 250; // Fixed short delay for the first trial to appear quickly
                console.log(`[GameContext Timer] Scheduling first trial advance (to trial 0) with a fixed short delay of ${initialDelayMs}ms.`);
            } else {
                // This branch would be for resuming a game that was already in progress,
                // or if somehow gameState became 'playing' when currentTrial was already >= 0.
                // In this case, apply full (weighted) variability for the next step.
                const randomVariabilityMs = getWeightedRandomVariability(maxRandomVariabilityOffsetMs);
                initialDelayMs = Math.max(100, isiMs + baseVariabilityOffsetMs + randomVariabilityMs); // baseVariabilityOffsetMs is likely 0
                console.log(`[GameContext Timer] Resuming or continuing game. Scheduling next trial advance with weighted variable delay of ${initialDelayMs.toFixed(0)}ms.`);
            }
            
            timerRef.current = setTimeout(advanceAndScheduleNext, initialDelayMs);
        }

        return () => { 
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; /* console.log("[GameContext Timer Effect] Timer cleared on cleanup or gameState change."); */ }
        };
    }, [gameState, endGame, logReplayEvent, setCurrentTrial, setGameState, setFeedbackMessage]);

    useEffect(() => {
        if (gameState === 'idle' && activeProfile && !challengeState.isActive && !isPlaygroundSession) {
            const profileNBack = activeProfile.currentNBack ?? 2;
            // --- MODIFICATION: Ensure difficulty is capped at 0.99 for display/logic consistency ---
            const profileDifficulty = Math.max(0, Math.min(0.99, activeProfile.currentDifficulty ?? 0.0));
            const profileStreak = activeProfile.consecutiveSuccesses ?? 0;
            if (nBack !== profileNBack) { setNBack(profileNBack); }
            if (Math.abs(difficulty - profileDifficulty) > 0.001) { setDifficulty(profileDifficulty); }
            if (consecutiveSuccesses !== profileStreak) { setConsecutiveSuccesses(profileStreak); }
            if (!saveToProfile) { setSaveToProfile(true); }
            if (showCountdown) { setShowCountdown(false); }
            if (pauseReason !== null) { setPauseReason(null); }
        }
    }, [
        activeProfile, gameState, challengeState.isActive, isPlaygroundSession,
        nBack, difficulty, consecutiveSuccesses, saveToProfile, showCountdown, pauseReason,
        setNBack, setDifficulty, setConsecutiveSuccesses, setSaveToProfile, setShowCountdown, setPauseReason
    ]);

    const value = useMemo(() => ({
        gameState, nBack, difficulty, sequence, currentTrial, totalTrials, gameResults,
        feedbackMessage, consecutiveSuccesses, isPlaygroundSession,
        showCountdown, challengeState, saveToProfile, pauseReason,
        pauseGame, resumeGame, clearPauseReason, playAfterCountdown, registerResponse,
        setManualSettings, startGame, startPlaygroundGame, endGame,
        logReplayEvent, cancelPreparation, setSaveToProfile, setNavigationHandler,
        enterChallenge, exitChallenge, completeChallenge, resetContextResponseArrays,
        isMatch, getStimulusDuration, getInstantFeedbackEnabled, getCoachingMessage, getFeatureIntroMessage,
        getVisualMatches, getAudioMatches, getUserVisualResponses, getUserAudioResponses,
        getPlaygroundSetting,
        // --- NEW ACTIONS ADDED TO VALUE ---
        goToPreviousNBackLevel,
        goToNextNBackLevelZero,
        // --- END NEW ACTIONS ---
        resumeAudio: audioManager.resume, playFeedbackSound,
        latestGameStateRef, sessionSettingsRef, currentTrialRef, isEndingRef
    }), [
        gameState, nBack, difficulty, sequence, currentTrial, totalTrials, gameResults,
        feedbackMessage, consecutiveSuccesses, isPlaygroundSession, showCountdown,
        challengeState, saveToProfile, pauseReason,
        pauseGame, resumeGame, clearPauseReason, playAfterCountdown, registerResponse,
        setManualSettings, startGame, startPlaygroundGame, endGame, logReplayEvent, cancelPreparation,
        setSaveToProfile, setNavigationHandler, enterChallenge, exitChallenge, completeChallenge,
        resetContextResponseArrays,
        isMatch, getStimulusDuration, getInstantFeedbackEnabled, getCoachingMessage, getFeatureIntroMessage,
        getVisualMatches, getAudioMatches, getUserVisualResponses, getUserAudioResponses,
        getPlaygroundSetting,
        // --- NEW ACTIONS ADDED TO DEPENDENCY ARRAY ---
        goToPreviousNBackLevel,
        goToNextNBackLevelZero,
        // --- END NEW ACTIONS ---
        playFeedbackSound, // playFeedbackSound was missing from dependencies, added it.
        latestGameStateRef, sessionSettingsRef, currentTrialRef, isEndingRef
    ]);

    return (
        <GameContext.Provider value={value}>
            {children}
        </GameContext.Provider>
    );
};